import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    ICustomField,
    IPagedResult,
    IPagedSearchParams,
    PFCoreEntities,
} from '@pf/shared-common';
import {
    VendorCreate,
    VendorRead,
    VendorSearchParams,
} from '../../entities/vendor.dto';

import { Injectable, inject } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { Operation } from 'fast-json-patch';
import {
    VendorCustomFieldsService,
    VendorsService,
} from '@control-tower/platform-core';
import { VendorStore } from './vendor.store';
import { ContactStore, IContact } from '@pf/shared/util-platform';

@Injectable()
export class VendorDataService extends AbstractDataService<
    VendorRead,
    VendorCreate
> {
    private vendorService = inject(VendorsService);
    private customFieldsService = inject(VendorCustomFieldsService);
    private contactStore = inject(ContactStore);

    protected EntityName = PFCoreEntities.Vendor;

    constructor() {
        const store = inject(VendorStore);
        const logger = inject(LoggerService);

        super(store, logger);
    }

    protected createObs$(body: VendorCreate): Observable<VendorRead> {
        return this.vendorService.vendorsCreate({
            vendorViewModelCreateBody: body,
        }) as Observable<VendorRead>;
    }

    protected deleteObs$(id: string): Observable<VendorRead> {
        return this.vendorService.vendorsDelete({
            id,
        }) as Observable<VendorRead>;
    }

    protected undoDeleteObs$(id: string): Observable<VendorRead> {
        return this.vendorService.vendorsUndoDelete({
            id,
        }) as Observable<VendorRead>;
    }

    protected getObs$(id: string): Observable<VendorRead> {
        return this.vendorService.vendorsReadById({
            id,
        }) as Observable<VendorRead>;
    }

    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<VendorRead> {
        return (
            this.vendorService.vendorsUpdatePatch({
                id,
                operation: operations,
            }) as Observable<VendorRead>
        ).pipe(
            map(x => {
                // TODO: figure out a better way to fix this
                // maybe always return booleans from api
                x.shouldReceiveNotifications ??= false;
                return x;
            })
        );
    }

    protected searchObs$(
        searchParams: VendorSearchParams
    ): Observable<IPagedResult<VendorRead>> {
        return this.vendorService.vendorsSearch(searchParams) as Observable<
            IPagedResult<VendorRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<VendorRead>>> {
        return this.vendorService.vendorsGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<VendorRead>>
        >;
    }

    getContacts$(id: string): Observable<IContact[]> {
        return this.vendorService
            .v1VendorsIdContactsGet({
                id,
            })
            .pipe(
                map(response => response.contacts as IContact[]),
                tap(contacts => {
                    contacts.forEach(contact =>
                        this.contactStore.add(contact, false)
                    );
                })
            ) as Observable<IContact[]>;
    }

    protected override createCustomFieldObs$(
        entityId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.vendorsCreateCustomField({
            entityId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override updateCustomFieldObs$(
        entityId: string,
        customFieldId: string,
        customField: ICustomField
    ): Observable<ICustomField & { id: string }> {
        return this.customFieldsService.vendorsCustomFieldsUpdatePut({
            entityId,
            id: customFieldId,
            customFieldViewModelCreate: {
                key: customField.key,
                value: customField.value,
            },
        }) as Observable<ICustomField & { id: string }>;
    }

    protected override searchCustomFieldsObs$(
        searchParams: IPagedSearchParams & { key?: string; value?: string }
    ): Observable<IPagedResult<ICustomField>> {
        return this.customFieldsService.vendorsSearchCustomFields(
            searchParams
        ) as Observable<IPagedResult<ICustomField>>;
    }
}
